import React from "react";
import { Button, Chip, Grid, Stack, Typography } from "@mui/material";
import ContentBox from "../../ContentBox/ContentBox";
import ButtonLoading from "../../Button/ButtonLoading";
import LegalbirdIoConfirm from "../../Modal/LegalbirdIoConfirm";
import { translate } from "../../../services/Translations/translatorService";
import { formatDateTime, shouldRenderExecutionAgentChip } from "./caseExecutionJobDetailsUtils";
import { ExecutionJob } from "./caseExecutionJobDetailsTypes";
import caseExecutionJobDetailsPageStyle from "./caseExecutionJobDetailsPageStyle";

type ExecutionJobDetailsCardProps = {
  executionJob: ExecutionJob;
  isExecuting: boolean;
  isRejecting: boolean;
  isExecutionAllowed: boolean;
  isRejectionAllowed: boolean;
  onBack: () => void;
  onExecute: () => Promise<void>;
  onReject: () => Promise<void>;
};

const ExecutionJobDetailsCard = ({
  executionJob,
  isExecuting,
  isRejecting,
  isExecutionAllowed,
  isRejectionAllowed,
  onBack,
  onExecute,
  onReject,
}: ExecutionJobDetailsCardProps) => {
  const [rejectConfirmationOpen, setRejectConfirmationOpen] = React.useState(false);

  return (
    <ContentBox headline={"Details"}>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant={"subtitle2"}>Titel</Typography>
          <Typography>{executionJob.jobTitle || "-"}</Typography>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant={"subtitle2"}>Agent</Typography>
          {shouldRenderExecutionAgentChip(executionJob.agentType) ? (
            <Chip
              label={executionJob.agentType}
              sx={(theme) => ({
                color: theme.palette.primary.contrastText,
              })}
            />
          ) : (
            <Typography>{executionJob.agentType || "-"}</Typography>
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant={"subtitle2"}>Status</Typography>
          <Chip
            size={"small"}
            label={translate(`executionJob.status.${executionJob.status}`, executionJob.status)}
            sx={caseExecutionJobDetailsPageStyle.jobStatusChipSxByStatus[executionJob.status]}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant={"subtitle2"}>Freigabe nötig</Typography>
          <Typography>{executionJob.requiresApproval ? "Ja" : "Nein"}</Typography>
        </Grid>
        <Grid size={12}>
          <Typography variant={"subtitle2"}>Beschreibung</Typography>
          <Typography>{executionJob.jobDescription || "-"}</Typography>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant={"subtitle2"}>Erstellt</Typography>
          <Typography>{formatDateTime(executionJob.created)}</Typography>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant={"subtitle2"}>Aktualisiert</Typography>
          <Typography>{formatDateTime(executionJob.updated)}</Typography>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Button color={"primary"} onClick={onBack}>
            Zur Job-Übersicht
          </Button>
        </Grid>
        <Grid
          size={{
          xs: 12,
          md: 6
          }}>
          <Stack sx={{ display: "flex", justifyContent: "flex-end", gap: 1, width: "100%" }}>
            <ButtonLoading
              variant={"contained"}
              onClick={onExecute}
              isLoading={isExecuting}
              disabled={!isExecutionAllowed || isRejecting}
              fullWidth
            >
              Job ausführen
            </ButtonLoading>
            <ButtonLoading
              variant={"outlined"}
              color={"error"}
              onClick={() => setRejectConfirmationOpen(true)}
              isLoading={isRejecting}
              disabled={!isRejectionAllowed || isExecuting}
              fullWidth
            >
              Job ablehnen
            </ButtonLoading>
          </Stack>
        </Grid>
      </Grid>
      <LegalbirdIoConfirm
        open={rejectConfirmationOpen}
        handleClose={() => setRejectConfirmationOpen(false)}
        headline={"Job ablehnen"}
        content={"Soll dieser Job wirklich abgelehnt werden? Dieser Vorgang kann nicht rückgängig gemacht werden."}
        confirmText={"Ablehnen"}
        handleConfirm={onReject}
      />
    </ContentBox>
  );
};

export default ExecutionJobDetailsCard;
